// Suppliers API (backend routes/supplier.routes.ts, mounted at /api/suppliers). Every call answers
// { success, data }; these helpers return `data`. SAP reads can take a while, hence the longer timeouts.
import apiService from './api.service';
import type {
  CreatedSupplier,
  CreationRow,
  SupplierCheck,
  SupplierConfig,
  SupplierCreator,
  SupplierInput,
  SupplierSource,
  ValidatedRow,
} from '../types/suppliers';

type Envelope<T> = { success: boolean; data: T; message?: string };
const data = <T>(res: Envelope<T>): T => res.data;
const BASE = '/suppliers';
const SAP_TIMEOUT = { timeout: 120000 };

/** The message the server gave for a failed call, else a fallback. */
export function supplierError(e: any, fallback: string): string {
  return String(e?.response?.data?.message || e?.message || fallback);
}

const supplierApi = {
  async config(companyId: number): Promise<SupplierConfig> {
    return data(await apiService.get<Envelope<SupplierConfig>>(`${BASE}/config`, { companyId }, SAP_TIMEOUT));
  },
  async check(companyId: number, vat: string, name: string): Promise<SupplierCheck> {
    return data(await apiService.get<Envelope<SupplierCheck>>(`${BASE}/check`, { companyId, vat, name }, SAP_TIMEOUT));
  },
  async create(
    companyId: number,
    supplier: SupplierInput,
    source: SupplierSource,
    extra: { fileName?: string; rowNum?: number } = {},
  ): Promise<CreatedSupplier> {
    // Longer than the server's own wait for SAP (120 s plus the fresh vendor read), so a vendor SAP created
    // is never reported here as a timeout that invites a second click.
    return data(await apiService.post<Envelope<CreatedSupplier>>(BASE, { companyId, source, supplier, ...extra }, { timeout: 300000 }));
  },
  async validate(companyId: number, rows: Array<Partial<SupplierInput> & { rowNum: number }>): Promise<{ rows: ValidatedRow[]; maxRows: number }> {
    return data(await apiService.post<Envelope<{ rows: ValidatedRow[]; maxRows: number }>>(`${BASE}/validate`, { companyId, rows }, SAP_TIMEOUT));
  },
  /** The Excel template, with drop-down lists read from the company's SAP. */
  async downloadTemplate(companyId: number): Promise<{ blob: Blob; fileName: string }> {
    const res = await (apiService as any).axiosInstance.get(`${BASE}/template`, { params: { companyId }, responseType: 'blob', timeout: 120000 });
    const disposition = String(res.headers?.['content-disposition'] || '');
    const match = /filename="?([^";]+)"?/i.exec(disposition);
    return { blob: res.data as Blob, fileName: match?.[1] || 'Suppliers_Template.xlsx' };
  },
  async log(companyId: number): Promise<CreationRow[]> {
    return data(await apiService.get<Envelope<CreationRow[]>>(`${BASE}/log`, { companyId }, { timeout: 60000 }));
  },
  /** Who may create suppliers in the company (any signed-in user may ask). */
  async creators(companyId: number): Promise<SupplierCreator[]> {
    return data(await apiService.get<Envelope<SupplierCreator[]>>(`${BASE}/creators`, { companyId }, { timeout: 60000 }));
  },
};

export default supplierApi;
